import { inject, Injectable } from '@angular/core';

import { BillingAddonType, BillingCheckoutResponse, BillingConsumeResponse, BillingCreditType, BillingEntitlements, BillingPlanTerm, BillingPortalResponse } from '@shared/models';
import { PocketbaseService } from '../pocketbase.service';

@Injectable({
  providedIn: 'root'
})
export class BillingApiService {
  private readonly pocketbaseService = inject(PocketbaseService);
  private readonly pb = this.pocketbaseService.getPocketBase();

  async getEntitlements(): Promise<BillingEntitlements> {
    return this.pb.send('/api/billing/entitlements', {
      method: 'GET'
    }) as Promise<BillingEntitlements>;
  }

  async createPlusCheckout(term: BillingPlanTerm = 'monthly'): Promise<BillingCheckoutResponse> {
    return this.pb.send('/api/billing/checkout/plus', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ term })
    }) as Promise<BillingCheckoutResponse>;
  }

  async createAddonCheckout(items: { addonType: BillingAddonType, amount: number }[]): Promise<BillingCheckoutResponse> {
    return this.pb.send('/api/billing/checkout/addon', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    }) as Promise<BillingCheckoutResponse>;
  }

  /**
   * Standalone pay-per-use purchase of a single correction (no Plus subscription
   * required). Grants one submission credit. Offered as the alternative to a
   * trial in the no-credits modal.
   */
  async createSingleCorrectionCheckout(): Promise<BillingCheckoutResponse> {
    return this.pb.send('/api/billing/checkout/single-correction', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    }) as Promise<BillingCheckoutResponse>;
  }

  /**
   * Standalone pay-per-use package "Eigene Klausur einreichen" (3,99 €, no Plus
   * subscription required). Grants one custom_case_package credit plus one
   * submission credit so the user can process their own Sachverhalt/Lösung and
   * get a single correction on it.
   */
  async createOwnExamCheckout(): Promise<BillingCheckoutResponse> {
    return this.pb.send('/api/billing/checkout/own-exam', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    }) as Promise<BillingCheckoutResponse>;
  }

  async createPortalSession(): Promise<BillingPortalResponse> {
    return this.pb.send('/api/billing/portal', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    }) as Promise<BillingPortalResponse>;
  }

  async consumeCredits(creditType: BillingCreditType, amount = 1, reason = 'manual_consume'): Promise<BillingConsumeResponse> {
    return this.pb.send('/api/billing/consume', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ creditType, amount, reason })
    }) as Promise<BillingConsumeResponse>;
  }
}
